import type { DevelopmentTask, DevelopmentTaskEvent } from '../../app/development-tasks'

/**
 * 流程卡的数据（修复任务闭环 §E）。
 *
 * 每一格的时间都来自一行事件（development_task_events），不来自 task.created_at，
 * 也不编。事件里认不出节点的行跳过；一个节点出现多次取最早那次
 * （第二轮再领到是新一格：按尝试号分）。
 *
 * 「当前节点」= 任务没到终态时的最后一格；耗时 = 下一格开始 - 这一格开始，
 * 最后一格 = 现在 - 开始。
 */
export type StageKey =
  | 'claimed'
  | 'delivered'
  | 'reviewing'
  | 'ci'
  | 'decision'
  | 'ready'
  | 'live'
  | 'fix_requested'
  | 'failed'
  | 'cancelled'

export interface Stage {
  readonly key: StageKey
  readonly at: string
  /** 第几轮尝试；从 attempt_claimed 的 payload 来，没有就是 0。 */
  readonly attempt: number
  readonly elapsedMs: number
  readonly current: boolean
  readonly tone: 'ok' | 'warn' | 'danger' | 'info'
}

const TERMINAL = new Set(['ready', 'live', 'failed', 'cancelled'])

function stageOf(event: DevelopmentTaskEvent): StageKey | null {
  const payload = (event.payload ?? {}) as Record<string, unknown>
  const to = typeof payload.to === 'string' ? payload.to : typeof payload.state === 'string' ? payload.state : ''
  if (event.type === 'attempt_claimed') return 'claimed'
  if (event.type === 'task_cancelled' || to === 'cancelled') return 'cancelled'
  if (/^review_/.test(event.type) || to === 'reviewing') return 'reviewing'
  if (to === 'ci_running') return 'ci'
  if (to === 'pending_approval' || event.type === 'needs_decision') return 'decision'
  if (to === 'gating' || /delivered|candidate/.test(event.type)) return 'delivered'
  if (to === 'fix_requested') return 'fix_requested'
  if (to === 'ready') return 'ready'
  if (to === 'failed') return 'failed'
  return null
}

function toneOf(key: StageKey): Stage['tone'] {
  switch (key) {
    case 'ready':
    case 'live':
      return 'ok'
    case 'decision':
    case 'fix_requested':
      return 'warn'
    case 'failed':
    case 'cancelled':
      return 'danger'
    default:
      return 'info'
  }
}

export function stagesOf(task: Pick<DevelopmentTask, 'state' | 'user_state'>, events: readonly DevelopmentTaskEvent[], now: Date): Stage[] {
  const rows: { key: StageKey; at: string; attempt: number }[] = []
  let attempt = 0
  for (const event of [...events].sort((a, b) => a.sequence - b.sequence)) {
    const key = stageOf(event)
    if (key === null) continue
    const payload = (event.payload ?? {}) as Record<string, unknown>
    if (key === 'claimed' && typeof payload.attempt_no === 'number') attempt = payload.attempt_no
    // 同一轮里同一个节点只记最早那次。
    if (rows.some((r) => r.key === key && r.attempt === attempt)) continue
    rows.push({ key, at: event.created_at, attempt })
  }
  // 「上线了」不是事件，是任务行上的事实（user_state=live）。时间用最后一行事件的，
  // 并且明说它是「上线时刻的下界」——没有那一行就不画。
  if (task.user_state === 'live' && rows.length > 0 && !rows.some((r) => r.key === 'live')) {
    rows.push({ key: 'live', at: rows[rows.length - 1]!.at, attempt })
  }
  const terminal = TERMINAL.has(task.state) || task.user_state === 'live'
  return rows.map((row, i) => {
    const start = Date.parse(row.at)
    const next = i + 1 < rows.length ? Date.parse(rows[i + 1]!.at) : now.getTime()
    const last = i === rows.length - 1
    return {
      key: row.key,
      at: row.at,
      attempt: row.attempt,
      elapsedMs: Number.isFinite(start) && Number.isFinite(next) ? Math.max(0, next - start) : 0,
      current: last && !terminal,
      tone: toneOf(row.key),
    }
  })
}

export function formatElapsed(ms: number): string {
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m${s % 60}s`
  const h = Math.floor(m / 60)
  return `${h}h${m % 60}m`
}
